/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import ActionModel from 'cio/models/action';
import { tracked } from '@glimmer/tracking';
import { Dropdown as BasicDropdown } from 'ember-basic-dropdown/components/basic-dropdown';
import calculatePosition from 'ember-basic-dropdown/utils/calculate-position';
import CampaignModel from 'cio/models/campaign';
import { task } from 'ember-concurrency';
import { timeout } from 'ember-concurrency';
import { isEventTargetInWormhole } from 'cio/utils/wormhole';
import Ember from 'ember';

interface CampaignWorkflowSummaryItemArgs {
  campaign: CampaignModel;
  group: {
    icon: string;
    type: string;
    actions: ActionModel[];
  };
}

const DROPDOWN_OPEN_CLOSE_TIMEOUT = 250;

export default class CampaignWorkflowSummaryItem extends Component<CampaignWorkflowSummaryItemArgs> {
  @tracked container: HTMLElement | null = null;
  @tracked icon: HTMLElement | null = null;
  @tracked dropdown: BasicDropdown | null = null;
  @tracked dropdownContent: HTMLElement | null = null;

  @tracked popoverPositionClass = 'fly-popover--top';
  @tracked popoverArrowLeftOffset = 0;

  @tracked sortedActions: ActionModel[] | null = null;

  @action
  registerContainer(element: HTMLElement) {
    this.container = element;

    this.findIconOffset();
  }

  @action
  registerIcon(iconElement: HTMLElement) {
    this.icon = iconElement;

    this.findIconOffset();
  }

  @action
  registerDropdown(dropdown: BasicDropdown) {
    this.dropdown = dropdown;
  }

  @action
  registerContent(contentElement: HTMLElement) {
    this.dropdownContent = contentElement;
  }

  @action
  findIconOffset() {
    if (this.container == null || this.icon == null) {
      return;
    }

    this.popoverArrowLeftOffset = this.icon.offsetLeft - this.container.offsetLeft + this.icon.offsetWidth / 2;
  }

  @action
  calculatePosition(trigger: HTMLElement, content: HTMLElement, destination: HTMLElement, options: any) {
    let position = calculatePosition(trigger, content, destination, options);

    if (position.verticalPosition === 'below') {
      this.popoverPositionClass = 'fly-popover--bottom';
    } else {
      this.popoverPositionClass = 'fly-popover--top';
    }

    return position;
  }

  @action
  onDropdownOpen() {
    if (this.sortedActions != null) {
      return;
    }

    if (this.args.campaign.traversalIndices == null) {
      this.args.campaign.findTraversalIndices();
    }

    let { traversalIndices } = this.args.campaign;

    this.sortedActions = [...this.args.group.actions].sort((a, b) => {
      return (traversalIndices?.[a.id] ?? 0) - (traversalIndices?.[b.id] ?? 0);
    });
  }

  @action
  openDropdown() {
    if (this.closeDropdownTask.isRunning) {
      this.closeDropdownTask.cancelAll();
    } else {
      this.openDropdownTask.perform();
    }
  }

  openDropdownTask = task({ keepLatest: true }, async () => {
    if (this.dropdown == null) {
      return;
    }

    if (!Ember.testing) {
      await timeout(DROPDOWN_OPEN_CLOSE_TIMEOUT);
    }

    this.dropdown.actions.open();
  });

  @action
  closeDropdown(event: MouseEvent) {
    // If the event target is within an ember-wormhole container, like
    // #fly-tooltips, it most likely means that we're hovering over a tooltip
    // within the popup (but the tooltip itself renders outside of the
    // popup's DOM context, so a mouseleave is triggered).
    // In this case, we don't want to close the popup.
    if (
      this.dropdownContent != null &&
      event.relatedTarget != null &&
      isEventTargetInWormhole(event, 'relatedTarget')
    ) {
      // There's an edge case where the "inner" tooltip's edges might overflow
      // out of the popup. It's possible to move the mouse into that tooltip,
      // carefully follow it along, and then leave the mouse outside both inner
      // tooltip and popup. This wouldn't trigger a "mouseleave" on the popup
      // itself anymore, so we attach a new mouseleave to the inner tooltip.
      (event.relatedTarget as Element).removeEventListener('mouseleave', this.closeDropdown);
      (event.relatedTarget as Element).addEventListener('mouseleave', this.closeDropdown);
      return;
    }

    if (this.openDropdownTask.isRunning) {
      this.openDropdownTask.cancelAll();
    }

    this.closeDropdownTask.perform();
  }

  closeDropdownTask = task({ keepLatest: true }, async () => {
    if (this.dropdown == null) {
      return;
    }

    if (!Ember.testing) {
      await timeout(DROPDOWN_OPEN_CLOSE_TIMEOUT);
    }

    this.dropdown.actions.close();
    this.dropdownContent = null;
  });
}
